'use client';

import React, { useMemo, useRef, useEffect, useState } from 'react';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import {
  faHospital,
  faCalendarClock,
  faPeopleGroup,
  faUser,
  faClinicMedical,
} from '@fortawesome/pro-solid-svg-icons';
import { HighlightedCriteria } from 'src/components/Strategy/shared';
import { useSelector } from 'react-redux';
import { selectSelectedProvider } from 'src/redux/slices/strategySlice';

interface PatientJourneyDetailsProps {
  engageStrategyDetails: any;
  isProfileView?: boolean;
}

interface EventConfig {
  title: string;
  icon: any;
  criteriaTemplate: string;
  criteriaInputs: Record<string, any>;
}

const PatientJourneyDetails = ({
  engageStrategyDetails,
  isProfileView = true,
}: PatientJourneyDetailsProps) => {
  const selectedProvider = useSelector(selectSelectedProvider);

  const contentType = selectedProvider?.content_type?.toLowerCase();
  const profileIndexEventIcon =
    contentType === 'hcp'
      ? faUser
      : contentType === 'clinic' || contentType === 'c'
      ? faClinicMedical
      : faHospital;

  const indexEventIcon = isProfileView ? profileIndexEventIcon : faPeopleGroup;

  const firstEventContentRef = useRef<HTMLDivElement>(null);
  const [firstEventLineHeight, setFirstEventLineHeight] = useState<number>(48); // Default h-12 (3rem = 48px)

  const targetEventType =
    engageStrategyDetails?.journey_definition?.target_event?.provider_type;
  const targetEventIcon =
    targetEventType === 'hcp' ? faPeopleGroup : faHospital;

  // Patient journey data can be in different structures
  const indexEventTemplate = engageStrategyDetails?.index_event_template;
  const targetEventTemplate = engageStrategyDetails?.target_event_template;
  const journeyDefinition = engageStrategyDetails?.journey_definition;

  const eventOrder = useMemo(() => {
    const mode = journeyDefinition?.delta?.mode || 'AFTER';

    const targetEvent: EventConfig = {
      title: 'Target Event',
      icon: targetEventIcon,
      criteriaTemplate: targetEventTemplate?.criteria_template || '',
      criteriaInputs: targetEventTemplate?.criteria_inputs || {},
    };

    const indexEvent: EventConfig = {
      title: 'Index Event',
      icon: indexEventIcon,
      criteriaTemplate: indexEventTemplate?.criteria_template || '',
      criteriaInputs: indexEventTemplate?.criteria_inputs || {},
    };

    return mode === 'BEFORE'
      ? [targetEvent, indexEvent]
      : [indexEvent, targetEvent];
  }, [
    indexEventTemplate,
    targetEventTemplate,
    journeyDefinition,
    indexEventIcon,
    targetEventIcon,
  ]);

  // Format the delta text
  const deltaText = useMemo(() => {
    const delta = journeyDefinition?.delta;
    if (!delta) return null;

    const { delta: deltaValue, units, mode } = delta;
    const direction = mode === 'BEFORE' ? 'preceding' : 'following';

    return `Within the ${direction} ${deltaValue} ${units}`;
  }, [journeyDefinition]);

  // Measure first event content height and adjust line height
  useEffect(() => {
    if (firstEventContentRef.current) {
      const contentHeight = firstEventContentRef.current.offsetHeight;
      // Line should extend from bottom of icon (40px) to accommodate content
      // Subtract a bit to make it shorter and not extend too far
      setFirstEventLineHeight(Math.max(32, contentHeight - 8));
    }
  }, [eventOrder, indexEventTemplate, targetEventTemplate]);

  const renderEvent = (
    event: EventConfig,
    showLineBelow: boolean,
    contentRef?: React.RefObject<HTMLDivElement>,
    lineHeight?: number
  ) => (
    <div className="relative flex items-start gap-3">
      <div className="relative flex flex-col items-center">
        <div className="w-10 h-10 rounded-full border border-neutral-200 flex items-center justify-center flex-shrink-0 bg-white z-10">
          <Icon icon={event.icon} className="text-sky-600" size="lg" />
        </div>
        {showLineBelow && (
          <div
            className="w-px bg-neutral-300"
            style={{ height: lineHeight ? `${lineHeight}px` : '3rem' }}
          />
        )}
      </div>
      <div className="flex flex-col gap-1 flex-1" ref={contentRef}>
        <h5 className="text-base font-semibold text-neutral-900">
          {event.title}
        </h5>
        {event.criteriaTemplate && (
          <div className="text-sm text-neutral-600">
            <HighlightedCriteria
              criteriaTemplate={event.criteriaTemplate}
              criteriaInputs={event.criteriaInputs}
            />
          </div>
        )}
      </div>
    </div>
  );

  const renderDelta = (showLineBelow: boolean) => (
    <div className="relative flex items-start gap-3">
      <div className="relative flex flex-col items-center w-10 self-start">
        <div className="w-6 h-6 rounded-full border border-neutral-300 flex items-center justify-center flex-shrink-0 bg-white z-10">
          <Icon icon={faCalendarClock} className="text-neutral-700" size="xs" />
        </div>
        {showLineBelow && <div className="w-px h-8 bg-neutral-300" />}
      </div>
      <div className="flex flex-col gap-1 flex-1">
        <h5 className="text-base font-semibold text-neutral-900">
          {deltaText}
        </h5>
      </div>
    </div>
  );

  return (
    <div className="flex flex-col gap-4">
      {engageStrategyDetails?.description && (
        <p className="text-base font-normal text-neutral-900">
          {engageStrategyDetails.description}
        </p>
      )}

      {/* Event visualization with connecting lines between circles */}
      <div className="flex flex-col">
        {/* First event */}
        <div className="">
          {renderEvent(
            eventOrder[0],
            true, // Always show line below first event
            firstEventContentRef,
            firstEventLineHeight
          )}
        </div>

        {/* Delta or simple connecting line */}
        {deltaText ? (
          renderDelta(true)
        ) : (
          <div className="relative flex items-start gap-3">
            <div className="relative flex flex-col items-center w-10">
              <div className="w-px h-6 bg-neutral-300" />
            </div>
          </div>
        )}

        {/* Second event */}
        {renderEvent(eventOrder[1], false)}
      </div>
    </div>
  );
};

export default PatientJourneyDetails;
